import React, { useState } from 'react';
import * as Avatar from '@radix-ui/react-avatar';
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import { useAuth, STATIC_ORGANIZATIONS } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const Header: React.FC = () => {
  const { logout, currentUser } = useAuth();
  
  // Get organization name if user is OrgAdmin
  const getUserOrganizationName = () => {
    if (currentUser?.role === 'OrgAdmin' && currentUser?.org_id) {
      const org = STATIC_ORGANIZATIONS.find(org => org.id === currentUser.org_id);
      return org?.name || 'Unknown Organization';
    }
    return null;
  };
  
  const organizationName = getUserOrganizationName();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  const handleProfileClick = () => {
    navigate('/profile');
    setOpen(false);
  };

  return (
    <header className="h-16 bg-header dark:bg-dark-header border-b border-gray-200 dark:border-dark-border flex items-center justify-between px-4 dark:text-dark-text">
      <div className="flex items-center">
        <div className="text-blue-500 mr-2">
          <svg 
            width="32" 
            height="32" 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path 
              d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" 
              fill="currentColor"
            />
            <path 
              d="M14 17H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" 
              fill="currentColor"
            />
          </svg>
        </div>
        <h1 className="text-xl font-semibold">DocuXray</h1>
      </div>
      <div className="flex items-center">
        <Dropdown.Root open={open} onOpenChange={setOpen}>
          <Dropdown.Trigger asChild>
            <button className="focus:outline-none" aria-label="User menu">
              <Avatar.Root className="inline-flex h-9 w-9 select-none items-center justify-center overflow-hidden rounded-full bg-blue-500 cursor-pointer hover:ring-2 hover:ring-blue-400">
                <div className="flex h-full w-full items-center justify-center text-white text-sm font-medium">
                  {currentUser?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
              </Avatar.Root>
            </button>
          </Dropdown.Trigger>
          
          <Dropdown.Portal>
            <Dropdown.Content
              className="min-w-[220px] bg-white dark:bg-gray-800 rounded-md shadow-lg p-1 z-50 border border-gray-200 dark:border-gray-700"
              sideOffset={5}
              align="end"
            >
              <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                  {currentUser?.name || 'User'}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {currentUser?.email || 'user@example.com'}
                </div>
                <div className="mt-1 flex items-center">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    currentUser?.role === 'SuperAdmin' 
                      ? 'bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-100' 
                      : 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100'
                  }`}>
                    {currentUser?.role || 'User'}
                  </span>
                  {organizationName && (
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                      {organizationName}
                    </span>
                  )}
                </div>
              </div>
              
              <Dropdown.Item 
                className="text-sm px-3 py-2 rounded flex items-center gap-2 text-gray-700 dark:text-gray-200 outline-none cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={handleProfileClick}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6 21V19C6 17.9391 6.42143 16.9217 7.17157 16.1716C7.92172 15.4214 8.93913 15 10 15H14C15.0609 15 16.0783 15.4214 16.8284 16.1716C17.5786 16.9217 18 17.9391 18 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Profile
              </Dropdown.Item>
              
              <Dropdown.Separator className="h-px bg-gray-200 dark:bg-gray-700 my-1" />
              
              <Dropdown.Item 
                className="text-sm px-3 py-2 rounded flex items-center gap-2 text-red-600 dark:text-red-400 outline-none cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={handleLogout}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Logout
              </Dropdown.Item>
            </Dropdown.Content>
          </Dropdown.Portal>
        </Dropdown.Root>
      </div>
    </header>
  );
};

export default Header;
